import * as React from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { SessionAudienceTypes } from 'config';
import moment from 'moment';
import eventLocationsValidation from '../../validation/event-locations-validation';
import {
  selectCourseOnlySessionMeta,
  selectLocations,
  selectLoader,
  selectSessionFee,
  selectSessionInformation,
} from '../../../../selector';
import {
  CourseOnlySessionStatus, IEventLocationsForm,
} from '../../../../interface';
import { createSessionActions } from '../../../../slice';
import EventLocationsForm from '../event-locations-form';

interface EventLocationsViewProps {
  editMode:boolean
}

export default function EventLocationsView({ editMode }: EventLocationsViewProps) {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const locations = useSelector(selectLocations);
  const { audienceType } = useSelector(selectSessionInformation);
  const { status } = useSelector(selectCourseOnlySessionMeta);
  const sessionFee = useSelector(selectSessionFee);

  const methods = useForm<IEventLocationsForm>({
    defaultValues: {
      locations: locations.length > 0 ? locations.map((item) => ({
        date: item.from ? moment(item.from).format('YYYY-MM-DD') : '',
        from: item.from ? moment(item.from).format('HH:mm:ss') : '',
        to: item.to ? moment(item.to).format('HH:mm:ss') : '',
        address: item.address,
      })) : [{
        date: '',
        from: '',
        to: '',
        address: '',
      }],
    },
    resolver: yupResolver(eventLocationsValidation),
    shouldFocusError: true,
  });

  React.useEffect(() => {
    methods.reset({
      locations: locations.length > 0 ? locations.map((item) => ({
        date: item.from ? moment(item.from).format('YYYY-MM-DD') : '',
        from: item.from ? moment(item.from).format('HH:mm:ss') : '',
        to: item.to ? moment(item.to).format('HH:mm:ss') : '',
        address: item.address,
      })) : [{
        date: '',
        from: '',
        to: '',
        address: '',
      }],
    });
  }, [locations, methods]);

  const onSubmit:
  SubmitHandler<IEventLocationsForm> = React.useCallback((data) => {
    const payload = data.locations.map((loc) => ({
      from: moment(`${loc.date} ${loc.from}`, 'YYYY-MM-DD HH:mm:ss').toISOString(),
      to: moment(`${loc.date} ${loc.to}`, 'YYYY-MM-DD HH:mm:ss').toISOString(),
      address: loc.address,
    }));
    dispatch(createSessionActions.AddLocations(payload));
    if (audienceType === SessionAudienceTypes.courseOnly) {
      if (status === CourseOnlySessionStatus.editing || editMode) {
        dispatch(createSessionActions.editSession(sessionFee));
      } else {
        dispatch(createSessionActions.createSession(sessionFee));
      }
    }
  }, [dispatch, audienceType, status, editMode, sessionFee]);

  return (
    <div>
      <FormProvider {...methods}>
        <EventLocationsForm
          onSubmit={onSubmit}
          loading={loading}
          audienceType={audienceType}
          courseOnlyStatus={status}
        />
      </FormProvider>
    </div>
  );
}
